import { faEraser } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import { InputCheckBox } from '@sharedComponents/InputCheckBox';
import Pagination from '@sharedComponents/Pagination';
import ToggleSwitch from '@sharedComponents/ToggleSwitch';
import Typography from '@sharedComponents/Typography';
import { LanguageNames } from '@utils/languageNames';
import { useEffect, useMemo, useState } from 'react';
import { useClearRefinements, usePagination, useRange, useRefinementList } from 'react-instantsearch';

import AlgoliaIndexSwitch from './AlgoliaIndexSwitch';
import AlgoliaSearchBar from './AlgoliaSearchBar';
import AlgoliaTestHits from './AlgoliaTestHits';

import * as style from './style.module.css';

const CustomAlgoliaList = ({ items, refine, isHierarchical = false, order = 'asc' }) => {
    return (
        <ul className={style.list}>
            {items.map((item) => (
                <li key={item.value} className={style.listItem}>
                    <label className={`${style.itemRow} ${item.count === 0 && !item.isRefined ? style.itemRowDisabled : ''}`}>
                        <InputCheckBox
                            checked={item.isRefined}
                            onChange={() => refine(item.value)}
                            disabled={item.count === 0 && !item.isRefined}
                        />
                        <span className={style.itemText}>{item.label}</span>
                        <span className={style.itemCount}>{item.count}</span>
                    </label>

                    {isHierarchical && item.data && item.isRefined && (
                        <div className={style.nestedList}>
                            <CustomAlgoliaList items={item.data} refine={refine} isHierarchical={true} order={order} />
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
};

const mergeWithAllItems = (currentItems, allItems) => {
    const currentByValue = new Map(currentItems.map((i) => [i.value, i]));
    return allItems
        .map((item) => {
            const current = currentByValue.get(item.value);
            return current ? current : { ...item, count: 0, isRefined: false, data: null };
        })
        .sort((a, b) => {
            if (a.count > 0 !== b.count > 0) return a.count > 0 ? -1 : 1;
            if (a.count !== b.count) return b.count - a.count;
            return a.label.localeCompare(b.label);
        });
};

/**
 * Composant pour les listes de raffinement Algolia
 */
const CustomRefinementList = (props) => {
    const { order, ...algoliaProps } = props;
    const sortBy = order === 'desc' ? ['name:desc'] : order === 'count' ? ['count:desc'] : ['name:asc'];
    const { items, refine } = useRefinementList({ ...algoliaProps, sortBy, limit: 100 });
    const [allItems, setAllItems] = useState([]);

    useEffect(() => {
        if (items.length > 0) {
            setAllItems((prev) => (prev.length === 0 ? items : prev));
        }
    }, [items]);

    const mergedItems = allItems.length > 0 ? mergeWithAllItems(items, allItems) : items;
    return <CustomAlgoliaList items={mergedItems} refine={refine} order={order} />;
};

const GENERAL_LEVELS_ORDER = ['Généraliste', 'Débutant', 'Intermédiaire', 'Confirmé', 'Expert', 'Positionnement'];
const SCHOOL_LEVELS_ORDER = ['A2', 'B2', 'C1', 'Bac+3', 'Bac+4/5', 'Post-bac'];

const sortByLevelOrder = (items, order) =>
    [...items].sort((a, b) => {
        const ia = order.indexOf(a.label);
        const ib = order.indexOf(b.label);
        if (ia === -1 && ib === -1) return a.label.localeCompare(b.label);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });

const FacetTitle = ({ children, marginTop = 'var(--space-m)' }) => (
    <div className={style.facetTitle} style={{ marginTop }}>
        <Typography fontSize="s" fontWeight="bold">
            {children}
        </Typography>
    </div>
);

const LevelFacets = ({ t }) => {
    const { items, refine } = useRefinementList({ attribute: 'tests.title.value', limit: 100 });
    const [allItems, setAllItems] = useState([]);

    useEffect(() => {
        if (items.length > 0) {
            setAllItems((prev) => (prev.length === 0 ? items : prev));
        }
    }, [items]);

    const mergedItems = allItems.length > 0 ? mergeWithAllItems(items, allItems) : items;

    const generalItems = sortByLevelOrder(
        mergedItems.filter((i) => GENERAL_LEVELS_ORDER.includes(i.label)),
        GENERAL_LEVELS_ORDER,
    );
    const schoolItems = sortByLevelOrder(
        mergedItems.filter((i) => SCHOOL_LEVELS_ORDER.includes(i.label)),
        SCHOOL_LEVELS_ORDER,
    );
    const otherItems = mergedItems.filter((i) => !GENERAL_LEVELS_ORDER.includes(i.label) && !SCHOOL_LEVELS_ORDER.includes(i.label));

    return (
        <>
            <FacetTitle>{t('Level')}</FacetTitle>
            <CustomAlgoliaList items={[...generalItems, ...otherItems]} refine={refine} />

            <FacetTitle marginTop="var(--space-s)">{t('Language level')}</FacetTitle>
            <CustomAlgoliaList items={schoolItems} refine={refine} />
        </>
    );
};

const LanguageRefinementList = ({ t }) => {
    const { items, refine } = useRefinementList({ attribute: 'tests.language', operator: 'or', limit: 100, sortBy: ['count:desc'] });
    const [allItems, setAllItems] = useState([]);

    useEffect(() => {
        if (items.length > 0) {
            setAllItems((prev) => (prev.length === 0 ? items : prev));
        }
    }, [items]);

    const mergedItems = allItems.length > 0 ? mergeWithAllItems(items, allItems) : items;
    const languageNames = LanguageNames(t);

    const mappedItems = mergedItems
        .map((item) => ({ ...item, label: languageNames[item.label] || item.label }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

    return <CustomAlgoliaList items={mappedItems} refine={refine} />;
};

const PedagogicalSwitch = ({ t }) => {
    const { items, refine } = useRefinementList({ attribute: 'tests.isPedagogical', limit: 10 });
    const trueItem = items.find((i) => i.value === 'true' || i.label === 'true');
    const isOn = trueItem?.isRefined ?? false;

    return (
        <div className={style.switchContainer}>
            <ToggleSwitch checked={isOn} onChange={() => trueItem && refine(trueItem.value)} size="s" disabled={!trueItem} />
            <span className={style.switchLabel}>
                {t('Pedagogical')} {trueItem ? `(${trueItem.count})` : '(0)'}
            </span>
        </div>
    );
};

/**
 * Composant de slider range personnalisé utilisant useRange
 */
const CustomRangeSlider = (props) => {
    const { start, range, canRefine, refine } = useRange(props);

    const safeMin = range.min === -Infinity ? 0 : range.min || 0;
    const safeMax = range.max === Infinity ? 100 : range.max || 100;
    const initialMin = start[0] !== undefined ? start[0] : safeMin;
    const initialMax = start[1] !== undefined ? start[1] : safeMax;

    const [startMin, startMax] = start;
    const [values, setValues] = useState({ min: initialMin, max: initialMax });

    useEffect(() => {
        setValues({
            min: startMin !== undefined ? startMin : safeMin,
            max: startMax !== undefined ? startMax : safeMax,
        });
    }, [startMin, startMax, safeMin, safeMax]);

    const handleMinChange = (e) => {
        const newMin = parseInt(e.target.value, 10);
        const newMax = Math.max(newMin, values.max);
        setValues({ min: newMin, max: newMax });
        refine([newMin, newMax]);
    };

    const handleMaxChange = (e) => {
        const newMax = parseInt(e.target.value, 10);
        const newMin = Math.min(values.min, newMax);
        setValues({ min: newMin, max: newMax });
        refine([newMin, newMax]);
    };

    if (!canRefine) {
        return null;
    }

    return (
        <div className={style.rangeSlider}>
            <div className={style.rangeValues}>
                <span>{values.min}</span>
                <span>{values.max}</span>
            </div>

            <div className={style.rangeTrackContainer}>
                <input
                    type="range"
                    min={safeMin}
                    max={safeMax}
                    value={values.min}
                    onChange={handleMinChange}
                    className={`${style.rangeInput} ${style.rangeInputMin}`}
                />
                <input
                    type="range"
                    min={safeMin}
                    max={safeMax}
                    value={values.max}
                    onChange={handleMaxChange}
                    className={`${style.rangeInput} ${style.rangeInputMax}`}
                />
                <div className={style.rangeTrack}>
                    <div
                        className={style.rangeFill}
                        style={{
                            left: `${((values.min - safeMin) / (safeMax - safeMin)) * 100}%`,
                            width: `${((values.max - values.min) / (safeMax - safeMin)) * 100}%`,
                        }}
                    />
                </div>
            </div>

            <div className={style.rangeLimits}>
                <span>{safeMin}</span>
                <span>{safeMax}</span>
            </div>
        </div>
    );
};

const DOMAIN_LABEL_OVERRIDES = { 'Spécifique Client': 'My tests' };

const CustomHierarchicalMenu = ({ t }) => {
    const lvl0 = useRefinementList({ attribute: 'hierarchicalTags.lvl0', operator: 'or', limit: 200, sortBy: ['name:asc'] });
    const lvl1 = useRefinementList({ attribute: 'hierarchicalTags.lvl1', operator: 'or', limit: 500, sortBy: ['name:asc'] });
    const [allLvl0, setAllLvl0] = useState([]);

    useEffect(() => {
        if (lvl0.items.length > 0) {
            setAllLvl0((prev) => (prev.length === 0 ? lvl0.items : prev));
        }
    }, [lvl0.items]);

    const baseItems = allLvl0.length > 0 ? allLvl0 : lvl0.items;
    const items = useMemo(
        () =>
            baseItems.map((item) => ({
                ...item,
                label: t(DOMAIN_LABEL_OVERRIDES[item.label]) || item.label,
                isRefined: lvl0.items.find((i) => i.value === item.value)?.isRefined ?? false,
                count: lvl0.items.find((i) => i.value === item.value)?.count ?? 0,
                data: lvl1.items
                    .filter((i) => i.value.startsWith(item.value + ' > '))
                    .map((child) => ({ ...child, label: child.value.split(' > ').pop() })),
            })),
        [baseItems, lvl0.items, lvl1.items, t],
    );

    const refine = (value) => {
        if (lvl0.items.find((i) => i.value === value) || allLvl0.find((i) => i.value === value)) lvl0.refine(value);
        else lvl1.refine(value);
    };

    return (
        <div>
            <CustomAlgoliaList items={items} refine={refine} isHierarchical={true} />
        </div>
    );
};

const DURATION_RANGES = [
    { label: '0-10 min', start: 0, end: 10 * 60 },
    { label: '10-20 min', start: 10 * 60, end: 20 * 60 },
    { label: '20-30 min', start: 20 * 60, end: 30 * 60 },
    { label: '30-45 min', start: 30 * 60, end: 45 * 60 },
    { label: '45+ min', start: 45 * 60 },
];

const DurationRefinement = ({ t }) => {
    const { items, refine } = useRefinementList({ attribute: 'tests.averageDuration', operator: 'or', limit: 1000 });

    const buckets = useMemo(
        () =>
            DURATION_RANGES.map((range, index) => {
                const members = items.filter((it) => {
                    const seconds = parseInt(it.value, 10);
                    if (Number.isNaN(seconds)) return false;
                    return seconds >= range.start && (range.end == null || seconds < range.end);
                });
                const refinedMembers = members.filter((m) => m.isRefined);
                return {
                    value: String(index),
                    label: range.label,
                    count: members.reduce((acc, m) => acc + (m.count || 0), 0),
                    isRefined: refinedMembers.length > 0,
                    members,
                    refinedMembers,
                };
            }),
        [items],
    );

    const visibleBuckets = buckets.filter((b) => b.count > 0 || b.isRefined);

    if (visibleBuckets.length === 0) return null;

    const refineBucket = (value) => {
        const bucket = buckets[parseInt(value, 10)];
        if (!bucket) return;
        const toToggle = bucket.refinedMembers.length > 0 ? bucket.refinedMembers : bucket.members;
        toToggle.forEach((member) => refine(member.value));
    };

    return (
        <>
            <FacetTitle>{t('Average duration')}</FacetTitle>
            <CustomAlgoliaList items={visibleBuckets} refine={refineBucket} />
        </>
    );
};

const ClearRefinementsButton = ({ t }) => {
    // Default excludedAttributes is ['query']: without emptying it the button would
    // clear the facets but leave the text search active.
    const { refine, canRefine } = useClearRefinements({ excludedAttributes: [] });
    return (
        <div className={canRefine ? style.clearVisible : style.clearHidden}>
            <Button ghost icon={faEraser} label={t('Reset filters')} action={refine} />
        </div>
    );
};

const AlgoliaFacets = ({ t }) => {
    return (
        <div className={style.facets}>
            <ClearRefinementsButton t={t} />

            <FacetTitle marginTop="0">{t('Domains')}</FacetTitle>
            <CustomHierarchicalMenu t={t} />

            <LevelFacets t={t} />

            <FacetTitle>{t('Test language')}</FacetTitle>
            <LanguageRefinementList t={t} />

            <DurationRefinement t={t} />

            <FacetTitle>{t('Pedagogical test')}</FacetTitle>
            <PedagogicalSwitch t={t} />
        </div>
    );
};

const AlgoliaPagination = ({ scrollTargetRef }) => {
    const { currentRefinement, nbPages, refine } = usePagination();

    if (nbPages <= 1) return null;

    const handleChange = (page) => {
        refine(page - 1);
        if (scrollTargetRef?.current) {
            scrollTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return <Pagination totalPages={nbPages} selectedPage={currentRefinement + 1} onChange={handleChange} />;
};

export {
    AlgoliaFacets,
    AlgoliaIndexSwitch,
    AlgoliaPagination,
    AlgoliaSearchBar,
    AlgoliaTestHits,
    CustomAlgoliaList,
    CustomRangeSlider,
    CustomRefinementList,
};
export { CATALOGUE_INDEX, CATALOGUE_INDEX_ALPHABETICAL } from './catalogueIndexes';

export default AlgoliaFacets;
